import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {connect} from 'react-redux';

import {getTimestamp, roundToNearest} from '../../../../utils/GeneralUtils';
import ProgressBar from '../../../progress-bar/ProgressBar';
import Icon from '../../../icon/Icon';
import Message, {getMessage, mapMessageStateToProps} from '../../../message/Message';
import WithFullScreen, {
    withFullScreenPropTypes
} from '../../../../hocs/with-fullscreen/WithFullScreen';
import {sizes, svgs, video} from '../../../../constants';

import styles from './HtmlPlayer.pcss';
import {appActions} from '../../../../redux/actions';
import {TrackingEventTypes} from '../../../../constants/Tracking';
import {
    trackVideoComplete,
    trackVideoFullScreen,
    trackVideoFullScreenExit,
    trackVideoLoad,
    trackVideoPause,
    trackVideoPlay,
    trackVideoMute,
    trackVideoUnmute,
    trackVideoCaptions
} from '../trackingHelpers';

/**
 * Html player component
 *
 * @class HtmlPlayerComponent
 * @extends PureComponent
 */
export class HtmlPlayerComponent extends PureComponent {
    static propTypes = {
        ...withFullScreenPropTypes,
        accentColor: PropTypes.string,
        autoplay: PropTypes.bool,
        className: PropTypes.string,
        hasControls: PropTypes.bool,
        hasFullScreen: PropTypes.bool,
        loop: PropTypes.bool,
        messages: PropTypes.object.isRequired,
        muted: PropTypes.bool,
        sources: PropTypes.arrayOf(
            PropTypes.shape({
                src: PropTypes.string,
                type: PropTypes.string
            })
        ).isRequired,
        poster: PropTypes.string,
        title: PropTypes.string,
        type: PropTypes.string.isRequired,
        trackEvent: PropTypes.func
    };

    static defaultProps = {
        hasControls: true,
        hasFullScreen: true
    };

    state = {
        buffered: 0,
        isDragging: false,
        disableDragging: false,
        hasPlayed: false,
        currentTime: 0,
        volume: 1,
        captionVisible: false,
        captionsAvailable: false,
        show: true
    };

    constructor(props) {
        super(props);
        this.state = {
            ...this.state,
            // TODO: Verify that this is an acceptably assumed state
            // There is no guarantee that this state will be correct simply
            // due to requesting autoplay (browser block, network issue, etc)
            isPlaying: props.autoplay,
            type: props.type
        };
    }

    componentDidMount() {
        if (this.video && this.video.duration) this.onLoad();
    }

    onLoad = () => {
        if (!this.video) return;
        this.setState({
            duration: this.video.duration
        });

        trackVideoLoad(this.props);
    };

    onProgress = () => {
        if (!this.video || !this.video.buffered) return;
        this.setState({
            buffered: this.video.buffered.length
                ? // Round buffering value to prevent extra rerenders
                  roundToNearest(this.video.buffered.end(0), 5)
                : 0
        });
    };

    onTimeUpdate = () => {
        if (!this.video) return;
        this.setState({
            currentTime: this.video.currentTime
        });
    };

    // This is a placeholder that can/should be overwritten derived classes.
    onSeeked() {}
    onPlaying() {
        trackVideoPlay(this.props);
    }
    onPause() {
        trackVideoPause(this.props);
    }

    onEnded() {
        this.setState({
            isPlaying: false
        });
        trackVideoComplete(this.props);
    }

    pauseVideo = () => {
        if (!this.video) return;

        this.video.pause();
        this.setState({
            isPlaying: false
        });
    };

    playVideo = () => {
        if (!this.video) return;

        this.video.play();
        this.setState({
            isPlaying: true
        });
    };

    toggleVideo = () => {
        if (!this.video) return;
        const shouldPlay = this.video.paused || this.video.ended;

        if (shouldPlay) {
            this.playVideo();
        } else {
            this.pauseVideo();
        }

        this.setState({
            hasPlayed: this.state.hasPlayed || !!shouldPlay
        });
    };

    /* Progress rail */

    onProgressRailUpdate = newValue => {
        if (!this.video) return;
        this.video.currentTime = newValue;
        // Set state immediately, instead of waiting for onTimeChange callback, for more immediate feedback
        this.setState({
            currentTime: newValue
        });
    };

    toggleDragging = isDragging => {
        this.setState({
            isDragging
        });
    };

    /* Volume */

    toggleMute = () => {
        const {volume} = this.state;

        const newVolume = volume === 0 ? this.lastVolume : 0;
        this.lastVolume = volume;
        this.setVolume(newVolume);

        if (newVolume === 0) {
            trackVideoMute(this.props);
        } else {
            trackVideoUnmute(this.props);
        }
    };

    setVolume = newValue => {
        if (!this.video) return;
        this.video.volume = newValue;
        this.setState({
            volume: newValue
        });
    };

    getVolumeIcon = () => {
        const {volume} = this.state;

        const {messages} = this.props;

        let ariaLabelCode;
        let icon;

        if (volume === 0) {
            ariaLabelCode = 'muted';
            icon = svgs.VOLUME_MUTE;
        } else if (volume <= 0.33) {
            ariaLabelCode = 'low';
            icon = svgs.VOLUME_LOW;
        } else if (volume <= 0.66) {
            ariaLabelCode = 'medium';
            icon = svgs.VOLUME_MEDIUM;
        } else {
            ariaLabelCode = 'high';
            icon = svgs.VOLUME_HIGH;
        }

        const ariaLabel = getMessage(messages, `diesel.common.video.volume.${ariaLabelCode}`);

        return <Icon ariaLabel={ariaLabel} icon={icon} size={sizes.XXXSMALL} />;
    };

    /* Fullscreen */

    toggleFullScreen = () => {
        const {exitFullScreen, isFullScreen, requestFullScreen} = this.props;

        if (isFullScreen) {
            exitFullScreen();
            trackVideoFullScreenExit(this.props);
            return;
        }

        requestFullScreen(this.wrapper);
        trackVideoFullScreen(this.props);
    };

    /* Timestamp */

    renderTimestamp() {
        const {currentTime, duration} = this.state;

        if (!duration) return null;

        const currentTimestamp = getTimestamp(currentTime);
        const numberLength = currentTimestamp.match(/\d/g).length;
        const colonLength = currentTimestamp.match(/:/g).length;

        // Give the current timestamp a set width
        // so text won't jump around do the width of specific digits
        const currentTimestampStyle = {
            width: 9 * numberLength + 2 * colonLength
        };

        return (
            <li className={styles.timestamp}>
                <span style={currentTimestampStyle}>{currentTimestamp}</span>
                {` / ${getTimestamp(duration)}`}
            </li>
        );
    }

    // These is a placeholder that can/should be overwritten derived classes.
    renderOverlay() {
        return null;
    }
    toggleCaptions(shown) {
        // NOTE: EpicPlayer has extra checks here to tell if we should correctly toggle captions
        this.setState({captionVisible: shown});

        trackVideoCaptions(this.props, shown);
    }
    renderTracks() {
        return null;
    }

    renderSources(sources) {
        return sources.map(({src, type: srcType}) => (
            <source key={srcType} src={src} type={srcType} />
        ));
    }

    render() {
        const {
            accentColor,
            autoplay,
            className,
            hasControls,
            hasFullScreen,
            isFullScreen,
            loop,
            messages,
            muted,
            poster,
            sources,
            title
        } = this.props;

        const {
            buffered,
            currentTime,
            duration,
            hasPlayed,
            isDragging,
            isPlaying,
            volume,
            captionsAvailable
        } = this.state;

        const progressLabel = getMessage(messages, 'diesel.common.video.controls.progress');
        const bufferingLabel = getMessage(messages, 'diesel.common.video.controls.buffering');
        const pauseLabel = getMessage(messages, 'diesel.common.video.controls.pause');
        const playLabel = getMessage(messages, 'diesel.common.video.controls.play');
        const volumeLabel = getMessage(messages, 'diesel.common.video.controls.volume');
        const enterFullScreenLabel = getMessage(messages, 'diesel.common.video.full_screen.enter');
        const exitFullScreenLabel = getMessage(messages, 'diesel.common.video.full_screen.exit');

        /* eslint-disable jsx-a11y/media-has-caption */
        return (
            <div
                className={cx(styles.wrapper, className, {
                    [styles.shouldShowControls]: isDragging || !isPlaying
                })}
                ref={el => {
                    this.wrapper = el;
                }}>
                <video
                    autoPlay={autoplay}
                    className={styles.video}
                    loop={loop}
                    muted={muted}
                    onClick={this.toggleVideo.bind(this)}
                    onLoadedMetadata={this.onLoad.bind(this)}
                    onProgress={this.onProgress}
                    onTimeUpdate={this.onTimeUpdate}
                    onSeeked={this.onSeeked.bind(this)}
                    onEnded={this.onEnded.bind(this)}
                    onPause={this.onPause.bind(this)}
                    onPlaying={this.onPlaying.bind(this)}
                    ref={el => {
                        this.video = el;
                    }}
                    poster={poster}
                    playsInline={!hasFullScreen}
                    title={title}>
                    {captionsAvailable && this.renderTracks()}
                    {this.renderSources(sources)}
                    <Message code="diesel.common.video.not_supported" />
                </video>
                {hasControls && (
                    <ul className={styles.controls}>
                        <li className={styles.progressWrapper}>
                            {duration && (
                                <ProgressBar
                                    shouldHideIndicator={!hasPlayed}
                                    onUpdate={this.onProgressRailUpdate.bind(this)}
                                    onToggleDragging={this.toggleDragging}
                                    backgroundColor={accentColor}
                                    primarySliderLabel={progressLabel}
                                    primarySliderValue={currentTime}
                                    primaryTotal={duration}
                                    secondarySliderLabel={bufferingLabel}
                                    secondarySliderValue={buffered}
                                    secondaryTotal={duration}
                                />
                            )}
                        </li>
                        <li>
                            <button className={styles.button} onClick={this.toggleVideo.bind(this)}>
                                {isPlaying ? (
                                    <Icon
                                        icon={svgs.PAUSE}
                                        ariaLabel={pauseLabel}
                                        size={sizes.XXXSMALL}
                                    />
                                ) : (
                                    <Icon
                                        icon={svgs.PLAY}
                                        ariaLabel={playLabel}
                                        size={sizes.XXXSMALL}
                                    />
                                )}
                            </button>
                        </li>
                        {captionsAvailable && (
                            <li>
                                <button
                                    className={cx(styles.button, styles.captionsButton)}
                                    onClick={this.toggleCaptions.bind(this)}>
                                    <div className={cx(styles.captionsText)}>CC</div>
                                    <div
                                        className={cx(styles.captionsLine, {
                                            [styles.captionsLineHidden]: !this.state.captionVisible
                                        })}
                                    />
                                </button>
                            </li>
                        )}
                        {!muted && (
                            <li className={cx(styles.volumeWrapper)}>
                                <button className={styles.button} onClick={this.toggleMute}>
                                    {this.getVolumeIcon()}
                                </button>
                                <span className={styles.volumeRail}>
                                    <span className={styles.volumeRailInner}>
                                        <ProgressBar
                                            onUpdate={this.setVolume}
                                            onToggleDragging={this.toggleDragging}
                                            backgroundColor={accentColor}
                                            primarySliderLabel={volumeLabel}
                                            primarySliderValue={volume}
                                            primaryTotal={1}
                                        />
                                    </span>
                                </span>
                            </li>
                        )}
                        {this.renderTimestamp()}
                        {hasFullScreen && (
                            <li className={styles.fullScreen}>
                                <button className={styles.button} onClick={this.toggleFullScreen}>
                                    {isFullScreen ? (
                                        <Icon
                                            ariaLabel={exitFullScreenLabel}
                                            icon={svgs.FULLSCREEN_EXIT}
                                            size={sizes.XXXSMALL}
                                        />
                                    ) : (
                                        <Icon
                                            ariaLabel={enterFullScreenLabel}
                                            icon={svgs.FULLSCREEN}
                                            size={sizes.XXXSMALL}
                                        />
                                    )}
                                </button>
                            </li>
                        )}
                    </ul>
                )}
                {this.renderOverlay()}
            </div>
        );
    }
}

const mapDispatchToProps = {
    trackEvent: params =>
        appActions.trackEvent(
            TrackingEventTypes.Interaction,
            'LauncherVideoPlayer',
            params.eventAction,
            params.eventLabel,
            params.eventValue
        )
};

// See "forwardRef": https://react-redux.js.org/api/connect#forwardref-boolean
// This is required by Redux so we can access the ref within the connected component.
export default connect(
    mapMessageStateToProps,
    mapDispatchToProps,
    null,
    {forwardRef: true}
)(WithFullScreen(HtmlPlayerComponent));
